// IdleLogout — ends the CRM session after a period of no interaction.
//
// The auth provider renews the access token on a timer whether or not anyone is
// at the keyboard, so without this an unattended CRM stays signed in for as long
// as the tab is open. Mounted by `RequireAuth` only while a session is live; it
// ends the session through `notifyUnauthorized`, the same single exit every
// other session end uses, so /login explains why the operator is there.

import { useEffect } from 'react';

import { notifyUnauthorized } from '../../lib/authStore';

export const IDLE_MS = 2 * 60 * 60_000; // 2 hours with no interaction
const CHECK_MS = 15_000;
const ACTIVITY = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const;

export function IdleLogout(): null {
  useEffect(() => {
    let last = Date.now();

    const bump = (): void => {
      last = Date.now();
    };
    // Wall-clock check, not a single long timeout: a sleeping machine or a
    // throttled background tab freezes timers, and `visibilitychange` re-checks
    // the moment the operator comes back.
    const check = (): void => {
      if (Date.now() - last >= IDLE_MS) notifyUnauthorized('idle');
    };

    ACTIVITY.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    document.addEventListener('visibilitychange', check);
    const interval = window.setInterval(check, CHECK_MS);

    return () => {
      ACTIVITY.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener('visibilitychange', check);
      window.clearInterval(interval);
    };
  }, []);

  return null;
}
